import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";
import type {
  DraftBatchResponse,
  PendingReviewResponse,
  ReviewSubmitBody,
  ReviewSubmitResponse,
} from "./types";

/**
 * HITL review queries + mutation. The first write surface in the SPA —
 * deliberately mirrors the existing `useJobs` / `useJob` shape rather
 * than introducing a new pattern. `apiFetch` was extended to take a
 * `method` + `body`; everything else is the established TanStack Query
 * idiom.
 */

/**
 * Fetch the draft batch for a job — every row in
 * `validatorResult.scoredRows` joined to the `tools_draft` Sheet, with
 * each row's existing decision (if any) overlaid. No polling — the
 * batch only mutates via `useSubmitReview`, which invalidates this
 * query on success.
 */
export function useDraftBatch(jobId: string | undefined) {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["draft-batch", jobId],
    enabled: Boolean(jobId),
    queryFn: ({ signal }) =>
      apiFetch<DraftBatchResponse>(client, `/api/jobs/${jobId}/draft-batch`, {
        signal,
      }),
  });
}

/**
 * Fetch the cross-job pending-review queue — every batch awaiting
 * review across all `pending_review` jobs, in one round trip. Each
 * batch mirrors the per-job `useDraftBatch` shape so the same
 * `<ReviewPanel>` renders both the standalone and queue surfaces.
 *
 * Used by both the `/review` page and the Layout's nav-tab count badge,
 * so TanStack's cache makes the badge effectively free across pages.
 */
export function usePendingReview() {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["review-pending"],
    queryFn: ({ signal }) =>
      apiFetch<PendingReviewResponse>(client, "/api/review/pending", {
        signal,
      }),
  });
}

/**
 * Submit a per-item review batch. Atomic at the orchestrator: every
 * decision lands as a `review_decision_recorded` audit row; if every
 * row in the batch is now decided, the orchestrator transitions the
 * job and (for the approved-any path) promotes approved rows to the
 * `tools_and_services` Sheet.
 *
 * On success we invalidate `["draft-batch", jobId]`, `["jobs", jobId]`,
 * and `["review-pending"]` — the panel re-fetches the locked-state
 * overlay, the JobDetail page picks up the status transition, and the
 * cross-job queue (plus its nav badge) refreshes so finalised batches
 * drop out and decided rows lock.
 */
export function useSubmitReview(jobId: string | undefined) {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviewSubmitBody) =>
      apiFetch<ReviewSubmitResponse>(client, `/api/jobs/${jobId}/review`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["draft-batch", jobId] });
      queryClient.invalidateQueries({ queryKey: ["jobs", jobId] });
      queryClient.invalidateQueries({ queryKey: ["review-pending"] });
    },
  });
}
